import React from 'react';
import { css } from '@emotion/react';
import { useFormContext } from 'react-hook-form';

// styles
import { halfSpacing } from 'styles/global_defaults/scaffolding';

// hooks
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

// components
import CustomRadioGroup from 'components/custom-radio-group';
import useAiRolePlayVoiceOptions from '../../hooks/use-ai-role-play-voice-options';
import useVoicePreview from '../../hooks/use-voice-preview';
import VoiceOption, { Voice } from './voice-option';

const VOICE_GRADIENT_COLORS = [
  { color1: '#8A87FF', color2: '#6554FF', color3: '#4F46E5' },
  { color1: '#60F3AB', color2: '#00CCBC', color3: '#009AC0' },
  { color1: '#86EFAC', color2: '#4ADE80', color3: '#22C55E' },
  { color1: '#FCA5A5', color2: '#F87171', color3: '#EF4444' },
  { color1: '#FDE047', color2: '#FACC15', color3: '#EAB308' },
  { color1: '#FBCFE8', color2: '#F472B6', color3: '#DB2777' },
  { color1: '#60F3AB', color2: '#00CCBC', color3: '#009AC0' },
  { color1: '#86EFAC', color2: '#4ADE80', color3: '#22C55E' },
  { color1: '#FF6B81', color2: '#FF4891', color3: '#ED2DED' },
  { color1: '#FFD166', color2: '#FFB03B', color3: '#FF8C00' },
  { color1: '#A5B4FC', color2: '#818CF8', color3: '#6366F1' },
  { color1: '#A5B4FC', color2: '#818CF8', color3: '#6366F1' },
] as const;

const voiceListStyles = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${halfSpacing}px;
`;

const voiceOptionContainerStyles = css`
  cursor: pointer;
`;

const VoiceList = () => {
  const params = useLecturePageParams();
  const voiceOptions = useAiRolePlayVoiceOptions(params.catalogId);
  const { setValue, watch } = useFormContext();
  const selectedVoiceId = watch('voiceOptionKey');

  // VoiceList mounts when voice mode is enabled; default to the first voice
  // so the submitted voiceOptionKey is never empty in voice mode
  React.useEffect(() => {
    if (!selectedVoiceId && voiceOptions.length) {
      setValue('voiceOptionKey', voiceOptions[0].key);
    }
  }, [selectedVoiceId, voiceOptions, setValue]);

  const voices: Voice[] = React.useMemo(
    () => voiceOptions.map((option, index) => ({
      id: option.key,
      previewAssetPath: option.previewAssetPath,
      gender: option.gender,
      ...VOICE_GRADIENT_COLORS[index % VOICE_GRADIENT_COLORS.length],
    })),
    [voiceOptions],
  );

  const { playingVoiceKey, togglePreview } = useVoicePreview();

  return (
    <CustomRadioGroup
      name='voiceOptionKey'
      className='mt-4'
      css={voiceListStyles}
      radios={voices.map((voice) => ({ value: voice.id, voice }))}
      activeValue={selectedVoiceId}
      onChange={(voiceId) => setValue('voiceOptionKey', voiceId, { shouldDirty: true })}
      optionContainerStyle={voiceOptionContainerStyles}
      radioInputTabIndex={-1}
      renderRadioButton={({ voice, checked }) => (
        <VoiceOption
          voice={voice}
          isSelected={checked}
          isPlaying={voice.id === playingVoiceKey}
          onSelect={() => setValue('voiceOptionKey', voice.id, { shouldDirty: true })}
          onTogglePreview={() => togglePreview(voice.id, voice.previewAssetPath)}
        />
      )}
    />
  );
};

export default VoiceList;
